/* eslint-disable react-refresh/only-export-components */
import { motion } from "framer-motion";
import Button from "@scripts/react/components/Button";
import { AnnotationSource } from "@rapid/types/annotation";
import { TooltipContainer } from "@scripts/react/components/Tooltip";
import { canvasHoverProps } from "@rapid/constants/motion";
import { useFilePreview } from "@rapid/contexts/FilePreviewContext";

export enum ReviewTab {
    DraftModel = "draft-model",
    MyAnnotations = "my-annotations"
}

export const REVIEW_TAB_TO_ANNOTATION_SOURCE: Record<ReviewTab, AnnotationSource> = {
    [ReviewTab.DraftModel]: AnnotationSource.Predictions,
    [ReviewTab.MyAnnotations]: AnnotationSource.Annotations
};

type ReviewTabsProps = {
    activeTab: ReviewTab;
    onTabChange: (tab: ReviewTab) => void;
    isGridView: boolean;
    onToggleGridView: () => void;
    visible: boolean;
    showTabs?: boolean;
    isFrameEditing?: boolean;
    onToggleFrameEditing?: () => void;
    showEditToggle?: boolean;
    editDisabled?: boolean;
    editLabel?: string;
};

function TabButton({
    tab,
    activeTab,
    onTabChange,
    children
}: {
    tab: ReviewTab;
    activeTab: ReviewTab;
    onTabChange: (tab: ReviewTab) => void;
    children: React.ComponentProps<typeof Button>["children"];
}) {
    const isActive = activeTab === tab;
    return (
        <Button
            type={isActive ? "dark-secondary" : "dark-ghost"}
            size="xsmall"
            className="border-none"
            onClick={() => onTabChange(tab)}
        >
            {children}
        </Button>
    );
}

export function ReviewTabs({
    activeTab,
    onTabChange,
    isGridView,
    onToggleGridView,
    visible,
    showTabs = true,
    isFrameEditing = false,
    onToggleFrameEditing,
    showEditToggle = false,
    editDisabled = false,
    editLabel = "Edit predictions"
}: ReviewTabsProps) {
    const { videoControls } = useFilePreview();
    const isPlaying = videoControls?.isPlaying ?? false;
    const handleEditToggle = () => {
        if (editDisabled) return;

        if (isPlaying) {
            videoControls?.videoRef.current?.pause();
        }
        onToggleFrameEditing?.();
    };

    return (
        <motion.div
            className="floatingContainer solid absolute right-4 top-4 z-50 flex items-center gap-2 p-1"
            {...canvasHoverProps(visible)}
        >
            <TooltipContainer
                title={isGridView ? "Switch to File View" : "Switch to Grid View"}
                type="titleOnly"
                customButton={
                    <Button
                        type="dark-ghost"
                        size="xsmall"
                        shape="square"
                        className="border-none"
                        onClick={onToggleGridView}
                    >
                        <i className={`fas ${isGridView ? "fa-image" : "fa-grid-2"}`} />
                    </Button>
                }
            />

            {showTabs && (
                <>
                    <div className="h-6 w-px bg-gray-600" />

                    <div className="flex items-center gap-1">
                        <TabButton
                            tab={ReviewTab.DraftModel}
                            activeTab={activeTab}
                            onTabChange={onTabChange}
                        >
                            Draft Model
                        </TabButton>
                        <TabButton
                            tab={ReviewTab.MyAnnotations}
                            activeTab={activeTab}
                            onTabChange={onTabChange}
                        >
                            Annotations
                        </TabButton>
                    </div>
                </>
            )}

            {showEditToggle && (
                <>
                    <div className="h-6 w-px bg-gray-600" />
                    <TooltipContainer
                        title={
                            editDisabled
                                ? editLabel
                                : isFrameEditing
                                  ? "Finish editing"
                                  : isPlaying
                                    ? `Pause and ${editLabel.toLowerCase()}`
                                    : editLabel
                        }
                        type="titleOnly"
                        customButton={
                            <Button
                                type={isFrameEditing ? "dark-secondary" : "dark-ghost"}
                                size="xsmall"
                                className="border-none"
                                onClick={handleEditToggle}
                                disabled={editDisabled}
                            >
                                <i className={`fas ${isFrameEditing ? "fa-check" : "fa-pen"}`} />
                                <span className="ml-1">{isFrameEditing ? "Done" : editLabel}</span>
                            </Button>
                        }
                    />
                </>
            )}
        </motion.div>
    );
}
